import { getAccessToken } from "./auth";

// Same-origin in production (Worker serves the SPA + /v1). Local Vite needs the API host.
const API_URL =
  import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? "http://localhost:8788" : "");

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Calls the Sebas API with a fresh bearer token.
 *
 * The token is fetched per request rather than cached: it is short-lived, and
 * `getJWTToken()` already returns a cached value until it nears expiry, so
 * caching again here would only risk sending a stale one.
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();

  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    // The API always sends { error }, but a proxy or a crash might not.
    const detail = await res
      .json()
      .then((b) => (b as { error?: string }).error)
      .catch(() => null);
    throw new ApiError(res.status, detail ?? `${res.status} ${res.statusText}`);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export type Profile = {
  id: string;
  displayName: string | null;
  avatarUrl: string | null;
  timezone: string;
  createdAt: string;
};
